import { apiClient } from '../api';
import { logError } from '../utils/logger';

/**
 * Client for `hp-be-trust-safety-service` — the dedicated Trust & Safety
 * service. The gateway mounts it at `/trust-safety/api` (dev) or `/api` (prod);
 * `VITE_APP_BACKEND_TRUST_SAFETY` overrides the base when set.
 *
 * Reports are authenticated-only and deliberately NOT business-scoped: the
 * `apiClient` interceptor only attaches `X-Active-Business-Id` to the
 * business-scoped prefixes in api/index.ts, and this path is not one of them.
 */

export type ReportTargetType =
  | 'provider'
  | 'business'
  | 'service'
  | 'review'
  | 'booking';

export type ReportReason =
  | 'fraud'
  | 'inappropriate'
  | 'no_show'
  | 'safety'
  | 'quality'
  | 'other';

export interface SubmitReportPayload {
  targetType: ReportTargetType;
  targetId: string;
  reason: ReportReason;
  description?: string;
  evidenceUrls?: string[];
}

const TRUST_SAFETY_BASE =
  import.meta.env.VITE_APP_BACKEND_TRUST_SAFETY || '/trust-safety/api';

/**
 * File a report against a provider / business / service / review / booking.
 * Matches the backend's POST /v1/reports body: `{ targetType, targetId,
 * reason, description?, evidenceUrls? }`. `businessId`/`providerId` are
 * resolved server-side from the session, so they are intentionally not sent.
 */
export const submitReport = async (
  payload: SubmitReportPayload
): Promise<unknown> => {
  try {
    const response = await apiClient.post(
      `${TRUST_SAFETY_BASE}/v1/reports`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    return response.data;
  } catch (error: any) {
    logError('Error submitting report:', { error });
    throw error;
  }
};
